import React, { useId, useState } from 'react';
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { CallOutlined, DeleteOutline, EditOutlined, EmailOutlined } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { Customer } from '../../types';
import RowActionsButton from '../common/RowActionsButton';

interface CustomerActionsMenuProps {
  customer: Customer;
  onEdit?: (customer: Customer) => void;
  onDelete?: (customer: Customer) => void;
  showContactActions?: boolean;
}

const CustomerActionsMenu: React.FC<CustomerActionsMenuProps> = ({ customer, onEdit, onDelete, showContactActions = false }) => {
  const { t } = useLanguage();
  const id = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const label = `${t('common.actions')}: ${customer.firstName} ${customer.lastName}`;
  const close = () => setAnchorEl(null);
  const phoneDialValue = customer.phone?.replace(/[^\d+]/g, '') || '';
  const hasDialablePhone = /\d/.test(phoneDialValue);
  const hasEmailAddress = Boolean(customer.email && customer.email.includes('@') && !/\s/.test(customer.email));
  const hasContactActions = showContactActions && (hasDialablePhone || hasEmailAddress);
  const hasManagementActions = Boolean(onEdit || onDelete);

  if (!hasContactActions && !hasManagementActions) return null;

  return (
    <Box
      onClick={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onContextMenu={event => event.stopPropagation()}
      sx={{ flexShrink: 0 }}
    >
      <RowActionsButton
        id={`${id}-button`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? `${id}-menu` : undefined}
        onClick={event => setAnchorEl(event.currentTarget)}
      />
      <Menu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { 'aria-labelledby': `${id}-button` } }}
      >
        {hasDialablePhone && <MenuItem key="call" component="a" href={`tel:${phoneDialValue}`} onClick={close}>
          <ListItemIcon><CallOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>{t('customers.call')}</ListItemText>
        </MenuItem>}
        {hasEmailAddress && <MenuItem key="email" component="a" href={`mailto:${customer.email}`} onClick={close}>
          <ListItemIcon><EmailOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>{t('customers.write')}</ListItemText>
        </MenuItem>}
        {hasContactActions && hasManagementActions && <Divider key="contact-divider" />}
        {onEdit && <MenuItem key="edit" onClick={() => { close(); onEdit(customer); }}>
          <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>}
        {onDelete && <MenuItem key="delete" onClick={() => { close(); onDelete(customer); }} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'inherit' }}><DeleteOutline fontSize="small" /></ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>}
      </Menu>
    </Box>
  );
};

export default CustomerActionsMenu;
